import React, { useState, useEffect, useRef } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import './AboutPage.css';

// Counter component with smooth count-up scroll animation matching Webflow GSAP physics
const CounterItem = ({ targetNumber, suffix = '', label, delayClass = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const duration = 1800;
          const startTime = performance.now();

          const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // easeOutExpo physics
            const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
            setCount(Math.floor(easeProgress * targetNumber));

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(targetNumber);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.25 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, targetNumber]);

  return (
    <div ref={counterRef} className={`counter-box reveal-up ${delayClass}`}>
      <div className="counter-value-box">
        <h2 className="counter-value-large">{count}</h2>
        <h2 className="counter-value-large counter-suffix">{suffix}</h2>
      </div>
      <p className="body-large">{label}</p>
    </div>
  );
};

// Inline CountUp scroll animation component matching Webflow data-count physics
const CountUp = ({ to = 20, duration = 1800 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const spanRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = performance.now();

          const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
            setCount(Math.floor(easeProgress * to));

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(to);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.2 }
    );

    if (spanRef.current) {
      observer.observe(spanRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, to, duration]);

  return <span ref={spanRef}>{count}</span>;
};

const AboutPage = ({ onNavigate }) => {
  const [activeFaq, setActiveFaq] = useState(0);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? -1 : index);
  };

  // IntersectionObserver for scroll-reveal animations across all sections
  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px',
    });

    const revealElements = document.querySelectorAll('.reveal-up, .text-reveal-up, .reveal-scale');
    revealElements.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, []);

  const faqData = [
    {
      question: 'What is included in each pricing plan?',
      answer:
        'Insurance needs vary, but most people should consider health, life, auto, and home coverage to stay financially protected in every situation.',
    },
    {
      question: 'How does an insurance policy work?',
      answer:
        'Insurance needs vary, but it’s essential for most people to consider health, life, auto, and home coverage to ensure comprehensive financial protection.',
    },
    {
      question: 'What is a premium and how is it calculated?',
      answer:
        'Insurance needs vary, but it’s crucial for most people to consider health, life, auto, and home coverage to ensure comprehensive financial protection.',
    },
    {
      question: 'What is a deductible in insurance?',
      answer:
        'Insurance needs vary, but it’s important for most people to consider health, life, auto, and home coverage to maintain financial security in all situations.',
    },
    {
      question: 'What does an insurance policy cover?',
      answer:
        'While insurance needs vary, most people should consider health, life, auto, and home coverage to ensure they remain financially secure in any situation.',
    },
    {
      question: 'How do I file an insurance claim?',
      answer:
        'While insurance needs vary, it’s crucial for most people to consider health, life, auto, and home coverage to maintain financial security.',
    },
    {
      question: 'How long does claim approval take?',
      answer:
        'While insurance needs differ, most individuals should consider health, life, auto, and home coverage to ensure comprehensive financial protection.',
    },
    {
      question: 'Can I cancel my policy anytime?',
      answer:
        'While insurance needs differ, it’s essential for most individuals to consider health, life, auto, and home coverage to ensure comprehensive financial protection.',
    },
  ];

  return (
    <div className="ensoro-about-page">
      {/* Top Navbar */}
      <Navbar isVisible={true} currentPage="about" onNavigate={onNavigate} />

      {/* Main Content Area */}
      <main className="main-wrap">
        {/* Section 1: Breadcrumb / Hero */}
        <section className="breadcrumb-section blog-page">
          <div className="main-container">
            <div className="breadcrumb-content left-align max700">
              <div className="breadcrumb-menu-block left-align text-reveal-up reveal-delay-1">
                <a
                  href="/"
                  className="breadcrumb-menu light-color"
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigate) onNavigate('home');
                  }}
                >
                  <span>home</span>
                </a>
                <div className="breadcrumb-menu-divider"></div>
                <div className="breadcrumb-menu current">
                  <span>About</span>
                </div>
              </div>
              <h1 className="heading-style-h1 text-reveal-up reveal-delay-2">
                Secure your future with trusted insurance.
              </h1>
            </div>
          </div>
          <div className="breadcrumb-overlay"></div>
        </section>

        {/* Section 2: Who We Are / About Section */}
        <section className="about-section">
          <div className="main-container">
            <div className="about-column two">
              <div className="left-column">
                <div className="about-thumb-box two reveal-scale">
                  <img
                    src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a1faa05966bb733703b667d_about-thumb2.webp"
                    alt="Insurance Consultant"
                    className="about-thumb"
                  />
                  <div className="counter-content reveal-up reveal-delay-2">
                    <div className="counter-value">
                      <h2><CountUp to={20} /></h2>
                      <h2 className="counter-accent">+</h2>
                    </div>
                    <p>Years of experience</p>
                  </div>
                  <img
                    src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a1faa378618e804e35c1565_about-thumb-overlay.webp"
                    alt="Overlay texture"
                    className="about-thumb-overlay-two reveal-up reveal-delay-1"
                  />
                </div>
              </div>
              <div className="right-column">
                <div className="about-content two">
                  <div className="mb24 text-reveal-up">
                    <div className="subtitle-block dark-color">
                      <img
                        src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a199df3debf5ef93c9cb8d6_subtitle-icon2.svg"
                        alt="Subtitle Icon"
                        className="subtitle-icon"
                      />
                      <span>Who we are</span>
                    </div>
                  </div>
                  <h2 className="text-reveal-up reveal-delay-1">
                    From a small agency to trusted insurance partner.
                  </h2>
                  <p className="body-large max800 text-reveal-up reveal-delay-2">
                    Started as a small team with vision, now trusted insurance partner delivering reliable protection and peace of mind.
                  </p>
                  <div className="keyword-list-block">
                    <div className="keyword-list reveal-up reveal-delay-3">
                      <p className="body-large">
                        Trusted Insurance Solutions for Every Life Stage
                      </p>
                      <p>
                        Providing dependable insurance coverage designed to protect families, businesses, health, and future financial stability.
                      </p>
                    </div>
                    <div className="keyword-list reveal-up reveal-delay-4">
                      <p className="body-large">
                        Reliable Protection Plans Backed by Experience Today.
                      </p>
                      <p>
                        Providing dependable insurance coverage solutions with expert guidance, personalized support, and long-term financial security.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Counter Section (Animated Count-up & Hover Effects) */}
        <section className="counter-section">
          <div className="main-container">
            <div className="counter-grid">
              <CounterItem targetNumber={24} suffix="k" label="Completed projects" delayClass="reveal-delay-1" />
              <CounterItem targetNumber={20} suffix="+" label="Years of experience" delayClass="reveal-delay-2" />
              <CounterItem targetNumber={99} suffix="%" label="Satisfied clients" delayClass="reveal-delay-3" />
              <CounterItem targetNumber={150} suffix="+" label="Expert advisors" delayClass="reveal-delay-4" />
            </div>
          </div>
        </section>

        {/* Section 4: Mission & Vision Section */}
        <section className="mission-vission-section">
          <div className="mission-vission-wrapper">
            <div className="main-container">
              <div className="mission-vission-column">
                <div className="left-column">
                  <div className="mission-vission-content">
                    <div className="mb24 text-reveal-up">
                      <div className="subtitle-block">
                        <img
                          src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a199df3c5962642375a36e0_subtitle-icon1.svg"
                          alt="Subtitle Icon"
                          className="subtitle-icon"
                        />
                        <span>Mission &amp; vision</span>
                      </div>
                    </div>
                    <h2 className="section-title text-reveal-up reveal-delay-1">
                      Strong vision <span className="hilight-text">driving tomorrow.</span>
                    </h2>
                    <div className="mission-vission-info">
                      <div className="mission-vission-block reveal-up reveal-delay-2">
                        <div className="heading-style-h6">Our mission</div>
                        <p className="mission-vission-description">
                          Our mission is to provide reliable insurance solutions that protect lives, assets, and financial futures.
                        </p>
                      </div>
                      <div className="mission-vission-block reveal-up reveal-delay-3">
                        <div className="heading-style-h6">Our vission</div>
                        <p className="mission-vission-description">
                          Our vission is to provide reliable insurance solutions that protect lives, assets, and financial futures.
                        </p>
                      </div>
                      <div className="mission-vission-block reveal-up reveal-delay-4">
                        <div className="heading-style-h6">Our goal</div>
                        <p className="mission-vission-description">
                          Our goal is to provide reliable insurance solutions that protect lives, assets, and financial futures.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="right-column">
                  <div className="mission-vission-thumb-box reveal-scale reveal-delay-2">
                    <img
                      src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a1fb6acd2ef9f4d1cf1ac93_mission-vission-thumb.webp"
                      alt="Mission and Vision"
                      className="mission-vission-thumb"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TEAM MEMBERS SECTION EXCLUDED AS SPECIFIED BY USER */}

        {/* Section 5: CTA / Award & Testimonial Section */}
        <section className="cta-section">
          <div className="cta-thumb"></div>
          <div className="cta-box">
            <div className="main-container">
              <div className="cta-wrapper reveal-up">
                <div className="cta-left">
                  <div className="cta-left-header">
                    <img
                      src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a1fc6d2ae02932f4c35a297_award1.png"
                      alt="#1 Detroit Life Insurance Forbes award badge"
                      className="award-thumb"
                    />
                    <img
                      src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a1fc6d2c59729022820dc6d_award2.png"
                      alt="Best Medical Loan Service Provider Winner"
                      className="award-thumb"
                    />
                  </div>
                  <div className="cta-left-footer">
                    <p>
                      “Our commitment is to provide trusted insurance solutions that protect families, secure futures, and deliver peace of mind every day.”
                    </p>
                    <div className="testimonial-author-box">
                      <div className="testimonial-author-block">
                        <img
                          src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a1d07528def5a8ce0a5c44f_author.png"
                          alt="Daniel Brooks"
                          className="testimonial-author"
                        />
                      </div>
                      <div className="testimonial-author-data">
                        <div className="heading-style-h6">Daniel Brooks</div>
                        <p>Insurance Advisor</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="cta-right">
                  <div className="heading-style-h3">Transparent insurance communication.</div>
                  <div className="counter-content posion-static">
                    <div className="counter-value">
                      <h2><CountUp to={99} /></h2>
                      <h2>%</h2>
                    </div>
                    <p>Years of experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 6: FAQ Section */}
        <section className="faq-section white-bg">
          <div className="main-container">
            <div className="faq-column">
              <div className="left-column">
                <div className="faq-content">
                  <div className="mb24 text-reveal-up">
                    <div className="subtitle-block dark-color">
                      <img
                        src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a199df3debf5ef93c9cb8d6_subtitle-icon2.svg"
                        alt="Subtitle Icon"
                        className="subtitle-icon"
                      />
                      <span>Faq</span>
                    </div>
                  </div>
                  <h2 className="faq-title dark text-reveal-up reveal-delay-1">
                    Clearing doubts and concerns.
                  </h2>
                  <p className="body-large max500 text-reveal-up reveal-delay-2">
                    Explore common questions about managing all tools in a single platform.
                  </p>
                  <div className="faq-content-bottom dark-color reveal-up reveal-delay-3">
                    <div className="faq-content-author">
                      <p className="body-large max380">
                        Book a quick discussion and we’ll show you how we do things.
                      </p>
                      <div className="author-grid">
                        <div className="testimonial-author-box">
                          <div className="testimonial-author-block">
                            <img
                              src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a1d07528def5a8ce0a5c44f_author.png"
                              alt="Daniel Brooks"
                              className="testimonial-author"
                            />
                          </div>
                          <div className="testimonial-author-data">
                            <div className="heading-style-h6">Daniel Brooks</div>
                            <p>Insurance Advisor</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="primary-button"
                          onClick={() => {
                            if (onNavigate) onNavigate('contact');
                          }}
                        >
                          <span className="button-text">Book A Call</span>
                          <div className="buttion-icon-box">
                            <img
                              src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a17d123299ebed0395d2c49_arrow-top-right.svg"
                              alt="Arrow top right"
                              className="button-icon"
                            />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="right-column">
                <div className="accordion-wrapper">
                  {faqData.map((item, idx) => {
                    const isOpen = activeFaq === idx;
                    return (
                      <div
                        key={idx}
                        className={`accordion-item ${isOpen ? 'active' : ''} reveal-up reveal-delay-${(idx % 4) + 1}`}
                        onClick={() => toggleFaq(idx)}
                      >
                        <div className="accordion-header">
                          <div className="accordion-title">
                            <div className="heading-style-h6">{item.question}</div>
                          </div>
                          <div className={`accordion-icon ${isOpen ? 'open' : ''}`}>
                            <img
                              src="https://cdn.prod.website-files.com/6a1713710d9ffeeda61c06e9/6a1fbff10999801b25261c09_plus-dark.png"
                              width="10"
                              alt="Toggle"
                              className="plus-icon"
                            />
                          </div>
                        </div>
                        <div
                          className="accordion-body"
                          style={{
                            maxHeight: isOpen ? '250px' : '0px',
                            transition: 'max-height 0.35s ease, opacity 0.35s ease',
                            opacity: isOpen ? 1 : 0,
                            overflow: 'hidden',
                          }}
                        >
                          <div className="accordion-content">
                            <p className="accordion-description dark-color">
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Existing Home Page Footer as requested */}
      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default AboutPage;
